import { API_URL, getAuthHeaders, handleApiError } from "./api";

export interface CashflowMonth {
  month: string; // YYYY-MM
  income: number;
  expenses: number;
  refunds: number;
  net: number;
  incomeByChannel: Record<string, number>;
  transactions: number;
}

export interface CashflowCoverageSlot {
  from: string;
  to: string;
  count: number;
}

export interface CashflowCoverage {
  accountId: string;
  role: "bank" | "card" | "wallet";
  aliases: string[];
  plaid: CashflowCoverageSlot | null;
  manual: CashflowCoverageSlot | null;
  transactions: number;
  name: string | null;
  institutionName: string | null;
  from: string | null;
  to: string | null;
}

export interface CashflowResponse {
  start: string | null;
  end: string | null;
  months: CashflowMonth[];
  totals: { income: number; expenses: number; net: number };
  excluded: {
    internalTransfers: number;
    cardPayments: number;
    walletActivity: number;
    pending: number;
    duplicateImports: number;
  };
  coverage: CashflowCoverage[];
  aliases: Record<string, string>;
  method: string[];
  generatedAt: string;
}

export type CoverageSource = "plaid" | "api" | "agent_export" | "human_import";

export interface CoverageMonth {
  plaid: number;
  api: number;
  agent_export: number;
  human_import: number;
}

export interface CoverageAccount {
  accountId: string;
  aliases: string[];
  name: string | null;
  institutionName: string | null;
  mask: string | null;
  type: string | null;
  subtype: string | null;
  from: string;
  to: string;
  total: number;
  months: Record<string, CoverageMonth>;
}

export interface CoverageResponse {
  range: { from: string; to: string };
  sources: CoverageSource[];
  accounts: CoverageAccount[];
  generatedAt: string;
}

export const analyticsAPI = {
  /**
   * Monthly income vs. expenses across all linked and imported accounts.
   * Pass `months` for a trailing window, or explicit `start`/`end` (YYYY-MM-DD).
   */
  getCashflow: async (params: {
    months?: number;
    start?: string;
    end?: string;
  }): Promise<CashflowResponse> => {
    const query = new URLSearchParams();
    if (params.months) query.set("months", String(params.months));
    if (params.start) query.set("start", params.start);
    if (params.end) query.set("end", params.end);
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/analytics/cashflow?${query.toString()}`,
      { headers }
    );
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to load cash-flow trajectory");
    }
    return response.json();
  },

  /**
   * Per-account, per-month transaction coverage grid: whether we have data
   * for each (account, month) and from which source(s).
   */
  getCoverage: async (): Promise<CoverageResponse> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/analytics/coverage`, {
      headers,
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to load account coverage timeline");
    }
    return response.json();
  },
};
